import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as supplierService from '../../services/supplierService';

export const fetchSuppliers = createAsyncThunk(
  'suppliers/fetchSuppliers',
  async (params, { rejectWithValue }) => {
    try {
      const res = await supplierService.getSuppliers(params);
      const body = res.data;
      return {
        suppliers: body.data || body.suppliers || [],
        totalPages: body.pagination?.totalPages || body.totalPages || 1,
        currentPage: body.pagination?.page || body.page || 1,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch suppliers');
    }
  }
);

export const fetchSupplier = createAsyncThunk(
  'suppliers/fetchSupplier',
  async (id, { rejectWithValue }) => {
    try {
      const res = await supplierService.getSupplier(id);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch supplier');
    }
  }
);

export const createSupplier = createAsyncThunk(
  'suppliers/createSupplier',
  async (supplierData, { rejectWithValue }) => {
    try {
      const res = await supplierService.createSupplier(supplierData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create supplier');
    }
  }
);

export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async ({ id, supplierData }, { rejectWithValue }) => {
    try {
      const res = await supplierService.updateSupplier(id, supplierData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update supplier');
    }
  }
);

export const deleteSupplier = createAsyncThunk(
  'suppliers/deleteSupplier',
  async (id, { rejectWithValue }) => {
    try {
      await supplierService.deleteSupplier(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete supplier');
    }
  }
);

export const fetchSupplierTransactions = createAsyncThunk(
  'suppliers/fetchSupplierTransactions',
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const res = await supplierService.getSupplierTransactions(id, params);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch transactions');
    }
  }
);

export const createSupplierPurchase = createAsyncThunk(
  'suppliers/createSupplierPurchase',
  async ({ id, purchaseData }, { rejectWithValue }) => {
    try {
      const res = await supplierService.recordSupplierPurchase(id, purchaseData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to record purchase');
    }
  }
);

export const createSupplierPayment = createAsyncThunk(
  'suppliers/createSupplierPayment',
  async ({ id, paymentData }, { rejectWithValue }) => {
    try {
      const res = await supplierService.recordSupplierPayment(id, paymentData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to record payment');
    }
  }
);

const initialState = {
  suppliers: [],
  supplier: null,
  loading: false,
  error: null,
  totalPages: 1,
  currentPage: 1,
  transactions: [],
};

const supplierSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    clearSupplier: (state) => {
      state.supplier = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers = action.payload.suppliers || action.payload.data || action.payload;
        state.totalPages = action.payload.totalPages || action.payload.pages || 1;
        state.currentPage = action.payload.page || 1;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.supplier = action.payload.supplier || action.payload;
      })
      .addCase(fetchSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.suppliers.unshift(action.payload.supplier || action.payload);
      })
      .addCase(createSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateSupplier.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.supplier || action.payload;
        const idx = state.suppliers.findIndex((s) => s._id === updated._id);
        if (idx !== -1) state.suppliers[idx] = updated;
        if (state.supplier?._id === updated._id) state.supplier = updated;
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        state.suppliers = state.suppliers.filter((s) => s._id !== action.payload && s.supplierId !== action.payload);
        if (state.supplier?._id === action.payload) state.supplier = null;
      })
      .addCase(fetchSupplierTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupplierTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.transactions || action.payload.data || action.payload;
      })
      .addCase(fetchSupplierTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSupplierPurchase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSupplierPurchase.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.supplier || action.payload;
        if (updated._id) {
          const idx = state.suppliers.findIndex((s) => s._id === updated._id);
          if (idx !== -1) state.suppliers[idx] = updated;
          if (state.supplier?._id === updated._id) state.supplier = updated;
        }
      })
      .addCase(createSupplierPurchase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSupplierPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSupplierPayment.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.supplier || action.payload;
        if (updated._id) {
          const idx = state.suppliers.findIndex((s) => s._id === updated._id);
          if (idx !== -1) state.suppliers[idx] = updated;
          if (state.supplier?._id === updated._id) state.supplier = updated;
        }
      })
      .addCase(createSupplierPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSupplier, clearError, setPage } = supplierSlice.actions;
export default supplierSlice.reducer;
